/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import Imgix from 'js/components/Imgix';

import { Grid, Typography2, breakpoints } from '@coursera/cds-core';
import type { GridProps } from '@coursera/cds-core';

import type { Maybe } from 'bundles/ent-website/__generated__/baseContentfulTypes';
import type {
  EntWebsiteContentColumnFragment,
  EntWebsiteHowFragment,
} from 'bundles/ent-website/__generated__/contentfulTypes';
import EntWebsiteRichText from 'bundles/ent-website/components/EntWebsiteRichText';
import ContentfulButton from 'bundles/ent-website/contentful/core/ContentfulButton';
import type { BlockLevelModule } from 'bundles/ent-website/contentful/core/Module';

import _t from 'i18n!nls/ent-website';

const styles = {
  icon: css`
    max-width: 100%;
    margin-bottom: 18px;
  `,
  eyebrow: css`
    color: var(--cds-color-neutral-primary-weak);
    font-weight: 700;
    line-height: 1;
    min-height: 1rem;
    text-transform: uppercase;
    letter-spacing: -0.03em;
    margin-bottom: 24px;

    ${breakpoints.down('xs')} {
      font-size: 14px !important;
    }
  `,
  title: css`
    letter-spacing: -0.03em;
    margin-bottom: 24px;
  `,
};

export const getGridItemSpanForColumn = (column: EntWebsiteContentColumnFragment, totalColumns: number): number => {
  // 33%
  if (totalColumns === 3) return 3;

  const mapping: Record<string, number> = {
    '30': 3,
    '40': 4,
    '50': 5,
    '60': 6,
    '70': 7,
  };

  const key: string = column?.columnSpan ?? '50';

  return key in mapping ? mapping[key] : 5;
};

export const getJustifyContentForCta = (ctaPosition?: Maybe<string>): GridProps['justifyContent'] => {
  if (ctaPosition == null) return 'flex-start';

  const mapping: Record<string, GridProps['justifyContent']> = {
    left: 'flex-start',
    middle: 'center',
    right: 'flex-end',
  };

  return ctaPosition in mapping ? mapping[ctaPosition] : 'flex-start';
};

type ExpandedContentColumnProps = {
  column: EntWebsiteContentColumnFragment;
  gridItemSpan: number;
  module: BlockLevelModule;
};

export function EpxandedContentColumn({ column, gridItemSpan, module }: ExpandedContentColumnProps) {
  return (
    <Grid
      container
      direction="column"
      css={css`
        background-color: ${column?.backgroundColor ? column?.backgroundColor : 'none'};
        padding: ${column?.padding == null ? '12px' : `${column?.padding}px`};
        border-radius: ${column?.borderRadius == null ? 0 : `${column?.borderRadius}px`};
        grid-column: span ${gridItemSpan};
        height: ${column?.height ?? 'fit-content'};

        ${breakpoints.down('sm')} {
          grid-column: none;
          margin-bottom: var(--cds-spacing-600);

          :last-of-type {
            margin-bottom: 0;
          }
        }
      `}
    >
      {column?.icon?.url != null && (
        <Imgix
          src={column?.icon?.url}
          alt={column?.icon?.description ?? _t('Icon')}
          css={styles.icon}
          width={column?.icon?.width ?? 200}
          height={column?.icon?.height ?? 200}
        />
      )}
      {column?.eyebrow && (
        <Typography2
          component="p"
          css={[
            styles.eyebrow,
            css`
              font-size: ${column?.eyebrowFontSize ?? 20}px;
              line-height: ${column?.eyebrowFontSize ?? 14}px;
              font-weight: ${column?.eyebrowFontWeight ?? 700};
              color: ${column?.eyebrowColor ?? 'var(--cds-color-grey-700)'};
            `,
          ]}
        >
          {column?.eyebrow}
        </Typography2>
      )}
      {column?.title && (
        <Typography2
          component="h3"
          variant="subtitleMedium"
          css={[
            styles.title,
            css`
              font-size: ${column?.titleFontSize ?? 48}px;
              line-height: ${column?.titleFontSize ?? 22}px;
              font-weight: ${column?.titleFontWeight ?? 600};
              color: ${column?.titleColor ?? 'var(--cds-color-grey-700)'};
            `,
          ]}
        >
          {column?.title}
        </Typography2>
      )}
      {column?.body && (
        <EntWebsiteRichText
          content={column?.body?.json}
          links={column?.body?.links}
          css={css`
            color: ${column?.bodyColor ?? 'var(--cds-color-neutral-primary)'};

            ${breakpoints.down('xs')} {
              p {
                font-size: 16px !important;
              }

              h2 {
                font-size: 40px !important;
              }
            }
          `}
        />
      )}
      {column?.cta?.ctaText && column?.cta?.ctaTarget && (
        <Grid
          container
          justifyContent={getJustifyContentForCta(column?.ctaPosition)}
          alignItems="flex-end"
          css={css`
            flex-grow: 1;
          `}
        >
          <ContentfulButton
            module={module}
            position={1}
            size="medium"
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-expect-error - check if variant exist or default to primary
            variant={column?.ctaVariant ?? 'primary'}
            fullWidth={column?.ctaFullWidth === true}
            href={column?.cta?.ctaTarget}
            target={column?.cta?.shouldOpenInNewWindow ? '_blank' : undefined}
            styles={css`
              height: fit-content;
            `}
          >
            {column?.cta?.ctaText}
          </ContentfulButton>
        </Grid>
      )}
    </Grid>
  );
}

export type Props = {
  columns: Array<Maybe<{ __typename?: 'EntWebsiteContentColumn' } & EntWebsiteContentColumnFragment>>;
  module: EntWebsiteHowFragment;
};

export function EpxandedContentColumns({ columns, module }: Props) {
  return (
    <Grid
      container
      css={css`
        display: grid;
        grid-auto-flow: column;
        grid-template-columns: repeat(${columns?.length === 3 ? 9 : 10}, 1fr);
        grid-gap: ${module?.expandedContentColumnsGridGap ?? 12}px;

        ${breakpoints?.down('sm')} {
          grid-auto-flow: row;
          grid-template-columns: none;
          grid-gap: 0;
        }
      `}
    >
      {columns?.map(
        (col) =>
          col && (
            <EpxandedContentColumn
              key={col?.sys?.id}
              column={col}
              gridItemSpan={getGridItemSpanForColumn(col, columns?.length)}
              module={module}
            />
          )
      )}
    </Grid>
  );
}

export default React.memo(EpxandedContentColumns);
